import { Instagram, MessageCircle, Mail, Phone } from "lucide-react";
import SmartLink from "@/components/SmartLink";
import { INSTAGRAM_URL, WA_DEFAULT, EMAIL, PHONE_DISPLAY, PHONE_TEL } from "@/data/site";

const SERVICE_LINKS = [
  { label: "Spotify Playlist Promotion", to: "/spotify-playlist-promotion" },
  { label: "Instagram Reels Promotion", to: "/instagram-reels-promotion" },
  { label: "YouTube Music Promotion", to: "/youtube-music-promotion" },
  { label: "Song Promotion", to: "/song-promotion" },
  { label: "Music Promotion", to: "/music-promotion" },
  { label: "Artist Promotion", to: "/artist-promotion" },
];

const COMPANY_LINKS = [
  { label: "Home", to: "/" },
  { label: "Why Us", to: "/#why-spotify" },
  { label: "Results", to: "/#results" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy", testid: "footer-privacy-link" },
  { label: "Terms & Conditions", to: "/terms-and-conditions", testid: "footer-terms-link" },
  { label: "Refund / Cancellation Policy", to: "/refund-policy", testid: "footer-refund-link" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" id="contact" className="relative border-t border-white/10 bg-ink-900/60">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon-purple to-transparent"
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <SmartLink to="/" className="leading-none" data-testid="footer-logo">
              <span className="block font-mono text-[10px] tracking-[0.35em] text-white/60">MILLIONAIRE</span>
              <span className="block font-display text-2xl font-extrabold tracking-tight text-gradient">CONNECTOR</span>
            </SmartLink>
            <p className="mt-4 font-display text-lg italic text-white/80">&ldquo;We Turn Songs Into Trends&rdquo;</p>
            <p className="mt-3 text-sm leading-relaxed text-white/50">
              Professional music promotion agency for Spotify playlist promotion, Instagram Reels campaigns and YouTube
              music promotion — trusted by 800+ clients across India.
            </p>
          </div>

          <nav aria-label="Services">
            <h3 className="overline-label mb-5">Services</h3>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((l) => (
                <li key={l.label}>
                  <SmartLink
                    to={l.to}
                    data-testid={`footer-service-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {l.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Company">
            <h3 className="overline-label mb-5">Company</h3>
            <ul className="space-y-3">
              {COMPANY_LINKS.map((l) => (
                <li key={l.label}>
                  <SmartLink
                    to={l.to}
                    data-testid={`footer-company-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {l.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="overline-label mb-5">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="inline-flex items-center gap-2.5 text-white/60 transition-colors hover:text-white"
                >
                  <Instagram className="h-4 w-4 text-neon-pink" aria-hidden="true" /> Instagram
                </a>
              </li>
              <li>
                <a
                  href={WA_DEFAULT}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="inline-flex items-center gap-2.5 text-white/60 transition-colors hover:text-white"
                >
                  <MessageCircle className="h-4 w-4 text-neon-blue" aria-hidden="true" /> WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2.5 text-white/60 transition-colors hover:text-white break-all"
                >
                  <Mail className="h-4 w-4 shrink-0 text-neon-purple" aria-hidden="true" /> {EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={PHONE_TEL}
                  data-testid="footer-phone-link"
                  className="inline-flex items-center gap-2.5 text-white/60 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 text-neon-pink" aria-hidden="true" /> {PHONE_DISPLAY}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 lg:flex-row">
          <p className="text-xs text-white/40">&copy; 2026 Millionaire Connector. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
            {LEGAL_LINKS.map((l) => (
              <SmartLink key={l.to} to={l.to} data-testid={l.testid} className="text-white/40 transition-colors hover:text-white">
                {l.label}
              </SmartLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
